<script setup>
import { computed, useSlots } from 'vue';

import HrCheckbox from './HrCheckbox.vue';
import HrRadio from './HrRadio.vue';

/**
 * Group of radio or checkbox options rendered from a data array.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Presenting a set of radio or checkbox options driven by a data array
 * @related HrRadio
 * @related HrCheckbox
 */
defineOptions({ name: 'HrOptionGroup' });

const props = defineProps({
  /** Current value (radio mode) or array of selected values (checkbox mode). */
  modelValue: {
    type: null,
    required: true,
  },
  /**
   * Array of option objects. Each option must have `value` and `label`.
   * An optional `disable` field disables that individual option.
   */
  options: {
    type: Array,
    required: true,
  },
  /**
   * Control type rendered for each option.
   * @values radio, checkbox
   */
  type: {
    type: String,
    default: 'radio',
    validator: (v) => ['radio', 'checkbox'].includes(v),
  },
  /** Disables all options in the group. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Renders options horizontally instead of stacked vertically. */
  inline: {
    type: Boolean,
    default: false,
  },
  /**
   * Switches all options to the error color role.
   */
  error: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to each individual HrRadio or HrCheckbox.
   * @deprecated Only for backwards compatibility with the legacy theme.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when the selection changes; payload is the new value (radio) or array (checkbox). */
  'update:modelValue': null,
});

useSlots();

const containerClass = computed(() => [
  'hr-option-group',
  props.inline && 'hr-option-group--inline',
]);

const role = computed(() => (props.type === 'radio' ? 'radiogroup' : 'group'));
</script>

<template>
  <div
    :class="containerClass"
    :role="role"
  >
    <template v-if="type === 'radio'">
      <hr-radio
        v-for="option in options"
        :key="option.value"
        :model-value="modelValue"
        :val="option.value"
        :label="$slots.label ? null : option.label"
        :disable="disable || !!option.disable"
        :error="error"
        :legacy-props="legacyProps"
        @update:model-value="emit('update:modelValue', $event)"
      >
        <!-- @slot label Custom label content for each option; receives the option object. -->
        <slot
          v-if="$slots.label"
          name="label"
          v-bind="option"
        />
      </hr-radio>
    </template>
    <template v-else>
      <hr-checkbox
        v-for="option in options"
        :key="option.value"
        :model-value="modelValue"
        :val="option.value"
        :label="$slots.label ? null : option.label"
        :disable="disable || !!option.disable"
        :error="error"
        :legacy-props="legacyProps"
        @update:model-value="emit('update:modelValue', $event)"
      >
        <slot
          v-if="$slots.label"
          name="label"
          v-bind="option"
        />
      </hr-checkbox>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.hr-option-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hr-option-group--inline {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px 16px;
}
</style>
